import { SiteUrls } from '@tableau/shared-urls';
import React from 'react';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom';

import { Messages } from '../../../../../gen-src/Localize';
import { WorkbookEmbeddedDatasourceFragment } from '../../../../../gen-src/queries';
import {
  CenteredMessage,
  FeatureFlagsContext,
  messageWithOrWithoutCount,
  SelectionProviderChildProps,
  SiteUrlsContext,
} from '../../../data-catalog-shared';
import { BrowserTitleUpdater, BrowserTitleUpdateType } from '../BrowserTitleUpdater';
import { FieldDetailsDialog } from '../FieldDetails/FieldDetailsDialog';
import { ErrorListProvider } from '../error/components/ErrorListProvider';
import { ErrorToasts } from '../error/components/ErrorToasts';
import { UnrecognizedUrl } from '../error/components/UnrecognizedUrl';
import { LineageItemType } from '../lineage/LineageItemType';
import { LineageItemConfig, lineageItemKey, LineageSection, LineageSectionType } from '../placePage/LineageConfiguration';
import { WorkbookLineageSubplaceLayout } from '../placePage/LineageSubplaceLayout';
import { memoizedGetWorkbookLineageConfiguration, WorkbookLineagePaneParams } from './WorkbookLineageConfiguration';
import { WorkbookLineageCounts, WorkbookLineageCountsQueryWrapper } from './WorkbookLineageCountsQueryWrapper';
import { WorkbookQueryWorkbooks, WorkbookQueryWrapper } from './WorkbookQueryWrapper';
import { WorkbookSubplaceParamsProvider } from './WorkbookSubplaceParamsProvider';



export interface WorkbookLineageSubplaceRouterProps {
  readonly vizportalUrlId: string;
}

export class WorkbookLineageSubplaceRouter extends React.Component<WorkbookLineageSubplaceRouterProps> {

  public override render(): JSX.Element {
    return (
      <SiteUrlsContext.Consumer>
        {urls =>
          <Route
            path={urls.workbookLineagePath(this.props.vizportalUrlId)}
            render={() => this.renderWorkbookLineageRoute(urls)}
          />
        }
      </SiteUrlsContext.Consumer>
    );
  }

  private readonly renderWorkbookLineageRoute = (urls: SiteUrls): JSX.Element => {
    return (
      <FeatureFlagsContext.Consumer>
        {
          featureFlags =>
            <Switch>
              {memoizedGetWorkbookLineageConfiguration(
                featureFlags.metricsDataCatalogEnabled,
                featureFlags.publishedConnectionsLineageEnabled,
                featureFlags.lensLineageEnabled).map(lineageSection =>
                  lineageSection.items.map((lineageItem, i) =>
                    <Route
                      key={i}
                      path={lineageItem.path(urls, this.props.vizportalUrlId)}
                      render={() => this.renderLineageItemRoute(urls, lineageItem, lineageSection)}
                    />
                  )
                )}
              <Redirect
                exact={true}
                from={urls.workbookLineagePath(this.props.vizportalUrlId)}
                to={urls.workbookLineageFieldsPath(this.props.vizportalUrlId)}
              />
              <UnrecognizedUrl
                backToButtonLabel={Messages.BackToFields()}
                backToButtonUrl={urls.workbookLineageFieldsPath(this.props.vizportalUrlId)}
              />
            </Switch>
        }
      </FeatureFlagsContext.Consumer>
    );
  }

  private getFieldsCountForSelectedEmbeddedDatasource(
    workbook: WorkbookQueryWorkbooks,
    selectedEmbeddedDatasourceId: string,
  ): number | null {
    const datasource = workbook.embeddedDatasources.find(d => d.id === selectedEmbeddedDatasourceId);
    if (datasource && datasource.fieldsConnection) {
      return datasource.fieldsConnection.totalCount;
    } else {
      return null;
    }
  }

  private readonly renderLineageItemRoute = (
    urls: SiteUrls,
    lineageItem: LineageItemConfig<WorkbookLineageCounts, WorkbookLineagePaneParams>,
    lineageSection: LineageSection<WorkbookLineageCounts, WorkbookLineagePaneParams>,
  ): JSX.Element => {
    return (
      <WorkbookSubplaceParamsProvider>
        {params =>
          <ErrorListProvider>
            <ErrorToasts />
            <WorkbookQueryWrapper vizportalUrlId={this.props.vizportalUrlId}>
              {workbook => {
                if (workbook.embeddedDatasources.length === 0) {
                  return <CenteredMessage message={Messages.EmptyWorkbookLineageMessage()} />;
                }

                const selectedEmbeddedDatasourceId = params.selectedEmbeddedDatasourceId || workbook.embeddedDatasources[0].id;
                const fieldsCountForSelectedEmbeddedDatasource =
                  this.getFieldsCountForSelectedEmbeddedDatasource(workbook, selectedEmbeddedDatasourceId);

                return (
                  <WorkbookLineageCountsQueryWrapper
                    vizportalUrlId={this.props.vizportalUrlId}
                    selectedEmbeddedDatasourceId={selectedEmbeddedDatasourceId}
                    selectedKeys={params.selectedFields}
                  >
                    {({ countsLoading, unfilteredCounts, filteredCounts }) =>
                      <>
                        <WorkbookLineageSubplace
                          vizportalUrlId={this.props.vizportalUrlId}
                          workbook={workbook}
                          lineageItem={lineageItem}
                          lineageSection={lineageSection}
                          countsLoading={countsLoading}
                          unfilteredCounts={unfilteredCounts}
                          filteredCounts={filteredCounts}
                          urlSearchValue={params.urlSearchValue}
                          clearFieldSelection={params.clearFieldSelection}
                          selectedKeys={params.selectedFields}
                          updateSelectionForKey={params.updateFieldSelection}
                          selectedEmbeddedDatasourceId={selectedEmbeddedDatasourceId}
                          updateEmbeddedDatasourceSelection={params.updateEmbeddedDatasourceSelection}
                          fieldsCountForSelectedEmbeddedDatasource={fieldsCountForSelectedEmbeddedDatasource}
                          embeddedDatasources={workbook.embeddedDatasources}
                        />
                        {lineageSection.sectionType === LineageSectionType.Anchor ?
                          <Route
                            path={urls.workbookLineageFieldPath(this.props.vizportalUrlId, ':fieldId')}
                            render={({ history, location, match }: RouteComponentProps<{ fieldId: string }>) =>
                              <FieldDetailsDialog
                                fieldId={match.params.fieldId}
                                makeFieldUrl={(fieldId) => urls.workbookLineageFieldUrl(this.props.vizportalUrlId, fieldId)}
                                onClose={() => history.replace({
                                  ...location,
                                  pathname: urls.workbookLineageFieldsPath(this.props.vizportalUrlId)
                                })}
                                canEditDescription={false}
                              />
                            }
                          />
                          : null
                        }
                      </>
                    }
                  </WorkbookLineageCountsQueryWrapper>
                );
              }}
            </WorkbookQueryWrapper>
          </ErrorListProvider>
        }
      </WorkbookSubplaceParamsProvider>
    );
  }

}

type WorkbookLineageSubplaceProps = {
  readonly vizportalUrlId: string;
  readonly workbook: WorkbookQueryWorkbooks;
  readonly lineageItem: LineageItemConfig<WorkbookLineageCounts, WorkbookLineagePaneParams>;
  readonly lineageSection: LineageSection<WorkbookLineageCounts, WorkbookLineagePaneParams>;
  readonly countsLoading: boolean;
  readonly unfilteredCounts: WorkbookLineageCounts | null;
  readonly filteredCounts: WorkbookLineageCounts | null;
  readonly urlSearchValue: string;
  readonly clearFieldSelection: () => void;
  readonly selectedEmbeddedDatasourceId: string;
  readonly updateEmbeddedDatasourceSelection: (embeddedDatasourceId: string) => void;
  readonly fieldsCountForSelectedEmbeddedDatasource: number | null;
  readonly embeddedDatasources: ReadonlyArray<WorkbookEmbeddedDatasourceFragment>;
} & SelectionProviderChildProps;

class WorkbookLineageSubplace extends React.PureComponent<WorkbookLineageSubplaceProps> {

  public override render(): JSX.Element {
    const selectedFields = Object.keys(this.props.selectedKeys);
    const hasFilter = selectedFields.length > 0;
    const filteredFieldId: string | null = hasFilter ? selectedFields[0] : null;
    const lineageCounts = this.props.filteredCounts ? this.props.filteredCounts : this.props.unfilteredCounts;
    return (
      <>
        <BrowserTitleUpdater
          name={this.props.workbook.name}
          browserTitleUpdateType={BrowserTitleUpdateType.TITLE_WITH_ENTITY_NAME_AND_SUBSPACE_NAME}
          subspaceName={Messages.LineageNavHeader()}
        />
        <WorkbookLineageSubplaceLayout
          activeLineageItemKey={lineageItemKey(this.props)}
          selectedAnchorItemIds={selectedFields}
          pane={this.props.lineageItem.paneFactory({
            title: messageWithOrWithoutCount(
              lineageCounts && this.props.lineageItem.count(lineageCounts),
              this.props.lineageItem.messagesForPane
            ),
            selectedKeys: this.props.selectedKeys,
            updateSelectionForKey: this.props.updateSelectionForKey,
            vizportalUrlId: this.props.vizportalUrlId,
            filteredFieldId,
            count: lineageCounts && this.props.lineageItem.count(lineageCounts),
            selectedEmbeddedDatasourceId: this.props.selectedEmbeddedDatasourceId,
            updateEmbeddedDatasourceSelection: this.props.updateEmbeddedDatasourceSelection,
            fieldsCountForSelectedEmbeddedDatasource: this.props.fieldsCountForSelectedEmbeddedDatasource,
            embeddedDatasources: this.props.embeddedDatasources,
            workbookLuid: this.props.workbook.luid,
          })}
          workbookId={this.props.vizportalUrlId}
          countsLoading={this.props.countsLoading}
          unfilteredCounts={this.props.unfilteredCounts}
          filteredCounts={this.props.filteredCounts}
          urlSearchValue={this.props.urlSearchValue}
          clearFieldSelection={this.props.clearFieldSelection}
          anchorName={this.props.workbook.name}
          anchorCount={this.props.embeddedDatasources.length}
          filteredOnItemType={hasFilter ? LineageItemType.Workbook : null}
          filteredItemName={this.props.filteredCounts?.filteredFieldName ?? null}
        />
      </>
    );
  }

}
